/*
  GRAVITUS — ricerca predittiva (snippets/gravitus-search.liquid).

  Pannello sotto il campo, tre stati:
    - campo vuoto      -> ricerche recenti, frequenti, settori (gia' nel DOM)
    - 2+ caratteri     -> risultati da /search/suggest con la sezione
                          `gravitus-search-results` (HTML reso dal server)
    - richiesta in corso -> aria-busy, i risultati precedenti restano visibili

  Pattern ARIA combobox + listbox: il focus resta nel campo, le frecce
  spostano aria-activedescendant sulle voci role="option".
  Nessuna libreria: Web Component vanilla come il resto del tema.
*/
(() => {
  const RECENT_KEY = 'gravitus:recent-searches';
  const RECENT_MAX = 5;
  const MIN_CHARS = 2;
  const DEBOUNCE = 180;

  const cache = new Map();

  const readRecent = () => {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((t) => typeof t === 'string') : [];
    } catch (e) {
      return [];
    }
  };

  const saveRecent = (term) => {
    const clean = (term || '').trim();
    if (clean.length < MIN_CHARS) return;
    try {
      const list = readRecent().filter((t) => t.toLowerCase() !== clean.toLowerCase());
      list.unshift(clean);
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {
      /* storage non disponibile (navigazione privata): si fa senza */
    }
  };

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  let uid = 0;

  class GravitusSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.gsearch__input');
      this.form = this.querySelector('.gsearch__form');
      this.panel = this.querySelector('.gsearch__panel');
      this.start = this.querySelector('[data-gsearch-start]');
      this.results = this.querySelector('[data-gsearch-results]');
      this.status = this.querySelector('[data-gsearch-status]');
      this.clearBtn = this.querySelector('.gsearch__clear');
      this.recentBox = this.querySelector('[data-gsearch-recent]');
      this.recentList = this.querySelector('[data-gsearch-recent-list]');
      if (!this.input || !this.panel) return;

      this.activeIndex = -1;
      this.lastTerm = '';
      this.controller = null;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => this.open());
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.form.addEventListener('submit', () => saveRecent(this.input.value));
      this.clearBtn?.addEventListener('click', () => {
        this.input.value = '';
        this.onInput();
        this.input.focus();
      });
      this.querySelector('[data-gsearch-clear-recent]')?.addEventListener('click', () => {
        try { window.localStorage.removeItem(RECENT_KEY); } catch (e) { /* niente */ }
        this.renderRecent();
        this.input.focus();
      });
      // Clic su una voce: memorizza il termine che l'ha prodotta.
      this.panel.addEventListener('click', (e) => {
        const option = e.target.closest('[role="option"]');
        if (!option) return;
        saveRecent(option.dataset.gsearchTerm || this.input.value);
      });

      this.onDocPointer = (e) => { if (!this.contains(e.target)) this.close(); };
      document.addEventListener('pointerdown', this.onDocPointer);

      // "/" porta il focus sulla ricerca (solo l'istanza visibile).
      this.onSlash = (e) => {
        if (e.key !== '/' || e.defaultPrevented) return;
        const tag = (document.activeElement && document.activeElement.tagName) || '';
        if (/INPUT|TEXTAREA|SELECT/.test(tag) || document.activeElement?.isContentEditable) return;
        if (!this.offsetParent) return;
        e.preventDefault();
        this.input.focus();
      };
      document.addEventListener('keydown', this.onSlash);

      this.syncClear();
      this.renderRecent();
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onDocPointer);
      document.removeEventListener('keydown', this.onSlash);
    }

    // ------------------------------------------------------------ stati
    open() {
      this.renderRecent();
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      this.showState();
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.classList.remove('is-open');
      this.activeIndex = -1;
    }

    showState() {
      const term = this.input.value.trim();
      const searching = term.length >= MIN_CHARS;
      this.start.hidden = searching;
      this.results.hidden = !searching;
    }

    syncClear() {
      if (this.clearBtn) this.clearBtn.hidden = this.input.value.length === 0;
    }

    renderRecent() {
      if (!this.recentList) return;
      const list = readRecent();
      this.recentBox.hidden = list.length === 0;
      this.recentList.innerHTML = '';
      const base = this.dataset.searchUrl || '/search';
      list.forEach((term) => {
        const li = document.createElement('li');
        li.setAttribute('role', 'presentation');
        const a = document.createElement('a');
        a.className = 'gsearch__chip gsearch__chip--recent';
        a.setAttribute('role', 'option');
        a.href = `${base}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        a.dataset.gsearchTerm = term;
        a.textContent = term;
        li.appendChild(a);
        this.recentList.appendChild(li);
      });
    }

    // ------------------------------------------------------------ ricerca
    onInput() {
      this.syncClear();
      if (this.panel.hidden) this.open();
      this.showState();
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      clearTimeout(this.timer);
      const term = this.input.value.trim();
      if (term.length < MIN_CHARS) return;
      this.timer = setTimeout(() => this.fetchResults(term), DEBOUNCE);
    }

    async fetchResults(term) {
      if (term === this.lastTerm && this.results.childElementCount) return;
      this.lastTerm = term;
      const url =
        `${this.dataset.url || '/search/suggest'}?q=${encodeURIComponent(term)}` +
        '&resources[type]=product,collection,page,article,query' +
        '&resources[limit]=6&resources[limit_scope]=each' +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.sku,variants.title,vendor,tag' +
        '&section_id=gravitus-search-results';

      if (cache.has(url)) {
        this.render(cache.get(url), term);
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(String(res.status));
        const text = await res.text();
        const html = new DOMParser().parseFromString(text, 'text/html').querySelector('.gsearch-r');
        const markup = html ? html.outerHTML : '';
        cache.set(url, markup);
        // L'utente potrebbe aver gia' cambiato termine: mostra solo l'ultimo.
        if (this.input.value.trim() === term) this.render(markup, term);
      } catch (e) {
        if (e.name !== 'AbortError') this.results.innerHTML = '';
      } finally {
        this.results.setAttribute('aria-busy', 'false');
      }
    }

    render(markup, term) {
      this.results.innerHTML = markup;
      this.highlight(term);
      const box = this.results.querySelector('.gsearch-r');
      const count = box ? Number(box.dataset.count || 0) : 0;
      const label = count > 0
        ? (this.dataset.labelResults || '').replace('[count]', count)
        : (this.dataset.labelNone || '');
      if (this.status) this.status.textContent = label;
    }

    /** Evidenzia le parole cercate nei titoli (solo nodi di testo). */
    highlight(term) {
      const words = term.split(/\s+/).filter((w) => w.length >= MIN_CHARS).map(escapeRegExp);
      if (!words.length) return;
      const re = new RegExp(`(${words.join('|')})`, 'gi');
      this.results.querySelectorAll('[data-gsearch-hl]').forEach((el) => {
        const text = el.textContent;
        if (!re.test(text)) return;
        re.lastIndex = 0;
        el.innerHTML = text
          .split(re)
          .map((part, i) => {
            const safe = part.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            return i % 2 === 1 ? `<mark>${safe}</mark>` : safe;
          })
          .join('');
      });
    }

    // ------------------------------------------------------------ tastiera
    options() {
      const scope = this.start.hidden ? this.results : this.start;
      return Array.from(scope.querySelectorAll('[role="option"]')).filter((el) => el.offsetParent !== null);
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        if (!this.panel.hidden) {
          e.preventDefault();
          this.close();
        } else if (this.input.value) {
          this.input.value = '';
          this.onInput();
        }
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
      if (this.panel.hidden && e.key !== 'Enter') { this.open(); return; }

      const opts = this.options();
      if (e.key === 'Enter') {
        const active = opts[this.activeIndex];
        if (active) {
          e.preventDefault();
          saveRecent(active.dataset.gsearchTerm || this.input.value);
          active.click();
        }
        return;
      }
      if (!opts.length) return;
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + step + opts.length) % opts.length;
      opts.forEach((el, i) => {
        if (!el.id) el.id = `gsearch-opt-${++uid}`;
        el.classList.toggle('is-active', i === this.activeIndex);
        el.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false');
      });
      const active = opts[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
  }

  if (!customElements.get('gravitus-search')) {
    customElements.define('gravitus-search', GravitusSearch);
  }
})();
